import Alert from "@mui/material/Alert";
import classNames from "classnames";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import {
  ResourceView,
  composeFilters,
  renderPrincipal,
  useResourceView,
  type Filter,
  type FilterDefinition,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks";
import { SessionRecord } from "@/shared/domain/sessions/SessionRecord";
import { useSessionTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  cellStyles,
  DataTable,
  buildColumnsForPreferences,
  colId,
  createColumnHelper,
  renderDashOr,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
  type ValueFormatterParams,
  type ValueGetterParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation/Goto";
import { formatNanoDuration, nanoSecToDate } from "@/shared/time";
import type { SearchQueryBody, SortDirection } from "@/types";

import styles from "./SessionsList.module.css";

const columnHelper = createColumnHelper<SessionRecord>();

/**
 * Normalize a session's nanosecond timestamp into a `Date` for `TimestampCell`,
 * or `null` (→ em dash) when absent. Sessions store these as `bigint`; the cell
 * only speaks instants.
 */
function nanosToDateOrNull(
  ns: bigint | number | undefined | null,
): Date | null {
  if (ns === undefined || ns === null) {
    return null;
  }
  return nanoSecToDate(BigInt(ns));
}

const durationColumn: ColDef<SessionRecord, bigint | null> = {
  colId: "duration",
  headerName: "Duration",
  flex: 1,
  sortable: true,
  cellClass: cellStyles.monoCell,
  valueGetter: (params: ValueGetterParams<SessionRecord>) => {
    const row = params.data;
    if (
      !row ||
      row.max_timestamp_ns === null ||
      row.min_timestamp_ns === null
    ) {
      return null;
    }
    return row.max_timestamp_ns - row.min_timestamp_ns;
  },
  valueFormatter: (
    params: ValueFormatterParams<SessionRecord, bigint | null>,
  ) => formatNanoDuration(params.value),
};

const STATIC_SESSION_COLUMNS: ColDef<SessionRecord>[] = [
  columnHelper.accessor("session_id", {
    headerName: "ID",
    width: 180,
    cellRenderer: (params: ICellRendererParams<SessionRecord, string>) => {
      const id = params.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Sessions}/${id}`}
          className={cellStyles.idCell}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    sortable: true,
    // String projection for clipboard export / quick-filter / screen readers;
    // the visible cell renders the placeholder through `cellRenderer` below.
    valueFormatter: (params) => params.value ?? "Untitled session",
    cellRenderer: (params: ICellRendererParams<SessionRecord, string>) => {
      const name = params.value;
      const id = params.data?.session_id;
      if (!name) {
        return <span className={cellStyles.placeholder}>Untitled session</span>;
      }
      if (!id) {
        return <span className={cellStyles.primaryContent}>{name}</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Sessions}/${id}`}
          className={cellStyles.primaryContent}
        >
          {name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.timestamp("min_timestamp_ns", {
    headerName: "Start",
    width: 120,
    sortable: true,
    variant: "precise",
    valueGetter: (params) => nanosToDateOrNull(params.data?.min_timestamp_ns),
  }),
  // Sort disabled: backend RoboQL catalog marks `max_timestamp_ns` non-sortable.
  // Re-enable once ENG-2522 enables it up server-side.
  columnHelper.timestamp("max_timestamp_ns", {
    headerName: "End",
    width: 120,
    sortable: false,
    variant: "precise",
    valueGetter: (params) => nanosToDateOrNull(params.data?.max_timestamp_ns),
  }),
  durationColumn,
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    flex: 1,
    sortable: false,
    cellRenderer: renderPrincipal,
  }),
  // Sort omitted: backend RoboQL `SESSION_FIELDS` catalog registers
  // `tags` with `is_sortable=False`; including `sortable: true` would
  // surface a click target that always 400s.
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 2,
    cellClass: cellStyles.tagsCell,
    // String projection for AG Grid's clipboard export, quick filter,
    // and screen-reader announcements; the visible cell still renders
    // through `cellRenderer` below.
    valueFormatter: (params: ValueFormatterParams<SessionRecord, string[]>) =>
      (params.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (params: ICellRendererParams<SessionRecord, string[]>) => (
        <TagGroup tags={params.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
];

const STATIC_SESSION_PICKER_OPTIONS: ColumnPickerOption[] =
  STATIC_SESSION_COLUMNS.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));

export interface SessionsListProps {
  /**
   * Applied to the outer container so the parent can size or position the list per page context.
   */
  className?: classNames.Argument;

  /**
   * Filters joined under an outer AND with the user's filter group, so
   * the user cannot widen results beyond them (e.g. the dataset tab pins
   * `dataset.dataset_id EQUALS <id>`). Hidden from the chip bar.
   *
   * The outer wrap is AND even when the user picks OR match mode; the
   * user's group nests inside so OR cannot dissolve the scope boundary.
   */
  scope?: Filter[];

  /**
   * Filterable fields the user can pick from. Omit to hide the filter
   * button and chip bar; the column-visibility picker and table still
   * render.
   */
  filterDefinitions?: FilterDefinition[];

  /**
   * Empty-state copy. Customize per-mount so dataset and device tabs can
   * scope the message (e.g. "No sessions for this dataset").
   */
  noRowsContent?: React.ReactNode;

  /**
   * Controls rendered at the right edge of the toolbar row, after the Filter
   * and Columns buttons. The Sessions page and dataset tab both pass their
   * "Create session" entry point here.
   */
  actions?: React.ReactNode;
}

/**
 * Paginated, filterable, sortable table of sessions. Submits a structured
 * query for the caller's org to obtain a `query_id`, then fetches a page
 * and total count off that id. Column order and page size persist per-user
 * via the shared session table preferences; sort is session-local.
 *
 * User filters always nest inside an outer AND with `scope`, so the
 * user's `matchMode` only governs how their own filters relate to each
 * other. See `composeFilters` for the composition rule.
 *
 * Renders no chrome of its own. Consumers wrap it in `<PageLayout>` or
 * mount it inside an outlet so the same component fits either context.
 */
export function SessionsList({
  className,
  scope,
  filterDefinitions,
  noRowsContent = "No sessions",
  actions,
}: SessionsListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useSessionTablePreferences();
  // Gate the Metrics filter pane behind the metrics release flag (defaults to
  // false while flag states load, so it stays hidden until confirmed enabled).
  const metricsEnabled = useCheckFeatureFlag("release-metrics");

  const customFields = useCustomFieldGridIntegration<SessionRecord>({
    entityType: TargetEntityType.Session,
    preferenceOrder: preferences.columns,
    savePreferences,
  });

  // Stored prefs are the source of truth; don't union with defaults on
  // mount — it would re-add columns the user explicitly removed.
  const columns = React.useMemo<ColDef<SessionRecord>[]>(
    () =>
      buildColumnsForPreferences<SessionRecord>({
        staticColumns: STATIC_SESSION_COLUMNS,
        dynamicColumns: customFields.customFieldColumns,
        preferenceOrder: customFields.effectivePreferenceOrder,
      }),
    [customFields.customFieldColumns, customFields.effectivePreferenceOrder],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_SESSION_PICKER_OPTIONS,
      ...customFields.columnPickerOptions,
    ],
    [customFields.columnPickerOptions],
  );

  // Append custom-field filters only when the mount already exposes filters;
  // appending unconditionally would force the filter UI onto mounts that
  // deliberately omit `filterDefinitions` to hide it.
  const effectiveFilterDefinitions = React.useMemo(
    () =>
      filterDefinitions?.length
        ? [...filterDefinitions, ...customFields.filterDefinitions]
        : filterDefinitions,
    [filterDefinitions, customFields.filterDefinitions],
  );

  const view = useResourceView<SessionRecord>({
    getRowId: (row) => row.session_id,
    initialSort: { columnId: "min_timestamp_ns", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });

  // Wrap user filters under an outer AND with `scope` so a dataset or
  // device tab can pin the list to its entity (passed through `scope`)
  // without exposing those predicates as user-editable chips. The user's
  // `matchMode` only governs how their own filters relate to each other;
  // an `OR` selection cannot dissolve the scope boundary. See
  // `composeFilters` and the `scope` prop's contract.
  const queryCondition = React.useMemo(() => {
    const structured =
      view.filters.type === "structured"
        ? view.filters
        : { filters: [] as Filter[], matchMode: "AND" as const };
    return composeFilters(
      scope ?? [],
      structured.filters,
      structured.matchMode,
    );
  }, [scope, view.filters]);

  // Omit the sort fields when the user has cleared the sort so the
  // server falls back to its own default ordering rather than receiving
  // an explicit "no sort" directive.
  const query: SearchQueryBody = React.useMemo(
    () => ({
      ...(queryCondition && { condition: queryCondition }),
      ...(view.table.sort && {
        sort_by: view.table.sort.columnId,
        sort_direction:
          view.table.sort.direction.toUpperCase() as SortDirection,
      }),
    }),
    [queryCondition, view.table.sort],
  );

  const queryKey = sessionQueryKeys.list(resolvedOrgId, query);

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Sessions,
    contentMode: QueryContentMode.RecordOnly,
    queryKey,
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<SessionRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  // Prefer the submit error: the page fetch is disabled until submit
  // returns a `query_id`, so any error it surfaces is downstream of an
  // already-surfaced submit failure.
  const fetchError = submitQuery.error ?? pageQuery.error;

  const emptyContent = view.hasActiveFilters
    ? "No matching sessions"
    : noRowsContent;

  return (
    <div className={classNames(styles.container, className)}>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <ResourceView.Root>
        <ResourceView.Actions>
          {effectiveFilterDefinitions?.length ? (
            <ResourceView.Filters
              filterDefinitions={effectiveFilterDefinitions}
              onFilterSelected={view.commit}
              metrics={metricsEnabled}
            />
          ) : null}
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          {actions ? (
            <div className={styles.trailingActions}>{actions}</div>
          ) : null}
        </ResourceView.Actions>
        {effectiveFilterDefinitions?.length ? (
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={effectiveFilterDefinitions}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
            metrics={metricsEnabled}
          />
        ) : null}
        <DataTable<SessionRecord>
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent={emptyContent}
          totalRowCount={countQuery.data}
        />
      </ResourceView.Root>
    </div>
  );
}
